import * as React from "react";
import {useEffect, useMemo} from "react";
import {Dialog, DialogContent, DialogFooter, DialogTitle, PushButton} from "@variocube/cube-ui";
import {style} from "typestyle";
import {useLocalization} from "../i18n";

export enum InvalidCodeType {
    Invalid = "Invalid",
    InvalidKey = "InvalidKey",
    Expired = "Expired"
}

export interface InvalidCode {
    type: InvalidCodeType;
    code: string;
}

interface InvalidCodeViewProps {
    invalidCode?: InvalidCode;
    onClose: () => void;
}

export function InvalidCodeDialog({invalidCode, onClose}: InvalidCodeViewProps) {
    const {t} = useLocalization();

    useEffect(() => {
        if(invalidCode) {
            const autoCloseTimer = setTimeout(() => {
                onClose();
            }, 20000);
            return () => {
                clearTimeout(autoCloseTimer);
            };
        }
    }, [invalidCode, onClose]);

    const errorType = useMemo<InvalidCodeType>(() => invalidCode ? invalidCode.type : InvalidCodeType.Invalid, [invalidCode]);

    const code = useMemo<string>(() => {
        if(invalidCode) {
            if(invalidCode.code.length < 20) {
                return invalidCode.code;
            }
            return invalidCode.code.substring(0, 16) + ' ...';
        }
        return '...';
    }, [invalidCode]);

	const {title, info} = useMemo(() => {
		let title = t('InvalidCodeTitle');
		let info = t('InvalidCodeInfo', {barCode: code});
		switch (errorType) {
			case InvalidCodeType.Expired:
				title = t('ExpiredCodeTitle');
				info = t('ExpiredCodeInfo', {barCode: code});
				break;
			case InvalidCodeType.InvalidKey:
				info = t('InvalidKeyCodeInfo', {barCode: code});
				break;
		}
		return { title, info };
	}, [code, errorType])

    return (
        <Dialog
			open={invalidCode != undefined}
			onBackdropClick={onClose}
		>
            <div className={style({ width: "45em"})}>
                <DialogTitle>
                    {title}
                </DialogTitle>
                <DialogContent>
                    <div className={style({ textAlign: 'center' })}>{info}</div>
                </DialogContent>
                <DialogFooter>
                    <PushButton onClick={onClose}>{t("Close")}</PushButton>
                </DialogFooter>
            </div>
        </Dialog>
    );
}
